import * as THREE from 'three';
import { gsap } from 'gsap';

import Style from './Style';

import { BASE_DURATION } from './Style';
import { getGoal } from '../../../../lib/utils/match.utils';

import vertexShader from '../shaders/dot.vert';
import fragmentShader from '../shaders/dot.frag';

export default class Candy extends Style {

	constructor(path, index, data) {
		super(path, index);

		this.name = 'Candy';

		this.settings 	= {
			colorIdx			: 0,
			radius 				: 15,
			count 				: 60,
			...this.settings
		};

		this.vec = new THREE.Vector3();
		this.dummy = new THREE.Object3D();

		this.tweens = [];

		this.initData(data);
		this.initMesh();
		this.initGUI();
		this.updatePositions();
		this.updateColors();
	}

	initMesh() {
		const { count } = this.settings;

		const geometry = new THREE.CircleGeometry(0.5, 32);

		const material = new THREE.ShaderMaterial({
			vertexShader,
			fragmentShader,
			depthTest: false,
		});

		const mesh = new THREE.InstancedMesh(geometry, material, count);

		this.object3D.add(mesh);
		this.mesh = mesh;

		// store tween properties
		for (let i = 0; i < count; i++) {
			this.tweens.push({ scl: 1 });
		}
	}

	updatePositions() {
		const { path, mesh, noise2D, vec, dummy, tweens } = this;
		const { count, radius } = this.settings;

		let t, scale, tween;

		if (!mesh) return;

		for (let i = 0; i < count; i++) {
			t = i / count;
			tween = tweens[i];

			path.curve.getPointAt(t, vec);

			scale = radius * tween.scl;

			dummy.position.copy(vec);
			dummy.scale.set(scale, scale, scale);
			dummy.updateMatrix();

			mesh.setMatrixAt(i, dummy.matrix);
		}

		mesh.instanceMatrix.needsUpdate = true;
	}

	updateColors() {
		const { count } = this.settings;
		const { index, mesh } = this;

		const goal 	= getGoal();
		const team 	= goal.teamDO;
		const kit 	= team.kits[goal.kit];
		const lng 	= kit.colors.length;
		const color = new THREE.Color();

		for (let i = 0; i < count; i++) {
			color.setStyle(kit.colors[i % lng]);
			mesh.setColorAt(i, color);
		}

		mesh.instanceColor.needsUpdate = true;
	}

	// ---------------------------------------------------------------------------------------------
	// MOTION
	// ---------------------------------------------------------------------------------------------

	preShow() {
		// hide all on start
		this.tweens.forEach((tween, i) => { tween.scl = 0; });
		this.updatePositions();
	}

	show() {
		const { tweens, index, speed } = this;
		const { count } = this.settings;
		let t, duration, delay;

		const timeline = gsap.timeline({ onUpdate: () => this.updatePositions() });
		const delayStep = BASE_DURATION * speed / count;

		timeline.clear();
		tweens.forEach((tween, i) => {
			t = i / tweens.length;
			delay = i * delayStep;

			timeline.set(tween, { scl: 0 }, 0);
			timeline.to(tween, { scl: 1, duration: 0.5, ease: 'expo.out' }, delay);
		});

		return timeline;
	}

	hide() {
		const { tweens, index, speed } = this;
		const { count } = this.settings;
		let t, duration, delay;

		const timeline = gsap.timeline({ onUpdate: () => this.updatePositions() });
		const delayStep = BASE_DURATION * speed / count;

		timeline.clear();
		tweens.forEach((tween, i) => {
			t = i / tweens.length;
			delay = i * delayStep;

			timeline.to(tween, { scl: 0, duration: 0.2, ease: 'expo.in' }, delay);
		});

		return timeline;
	}

	// ---------------------------------------------------------------------------------------------
	// GUI
	// ---------------------------------------------------------------------------------------------

	initGUI(parent) {
		const folder = super.initGUI(parent);
		const { settings } = this;

    if (!folder) return;

		// remove default children
		while (folder.children.length) folder.remove(folder.children[0]);

		folder.addInput(settings, 'count', 				{ min: 1, max: 200, step: 1 }).on('change', this.onCountChange.bind(this));
		folder.addInput(settings, 'radius', 			{ min: 1, max: 100, step: 1 }).on('change', this.updatePositions.bind(this));

		return folder;
	}

	onCountChange() {
		this.mesh.geometry.dispose();
		this.mesh.material.dispose();
		this.object3D.clear();

		this.initMesh();
		this.updatePositions();
		this.updateColors();
	}
}
